/**
 * share-wc: Social share component with native Web Share API and platform fallbacks
 *
 * Three-tier progressive enhancement:
 * - Tier 1 (native): Single "Share" button using navigator.share()
 * - Tier 2 (platforms): Individual platform buttons with share URLs
 * - Tier 3 (no JS): Static <a> links in light DOM work as-is
 *
 * @attr {string} url              - URL to share (default: location.href)
 * @attr {string} title            - Share title (default: document.title)
 * @attr {string} text             - Share description (default: meta description)
 * @attr {string} platforms        - Comma-separated platform list (default: "x,linkedin,bluesky,mastodon,whatsapp,email,copy")
 * @attr {string} variant          - Visual variant: "icon", "label", "icon-label" (default: "icon-label")
 * @attr {string} size             - Button size: "s", "m", "l" (default: "m")
 * @attr {string} label            - Label for native share button (default: "Share")
 * @attr {boolean} color           - Present = use platform brand colours
 * @attr {string} mastodon-instance - Mastodon instance (default: "mastodon.social")
 * @attr {string} tier             - Force tier: "native", "platforms" (overrides auto-detection)
 *
 * @example
 * <share-wc></share-wc>
 *
 * @example Force platform buttons even on devices with Web Share API
 * <share-wc tier="platforms" platforms="x,linkedin,copy"></share-wc>
 */

import { registerComponent } from '../../lib/bundle-registry.js';
import { VBElement } from '../../lib/vb-element.js';
import { copyText } from '../../utils/copy-init.js';
import { PLATFORMS, DEFAULT_PLATFORMS } from './platforms.js';

class ShareWc extends VBElement {
  /** @type {string | undefined} */
  #url;
  /** @type {string | undefined} */
  #title;
  /** @type {string | undefined} */
  #text;
  /** @type {string | undefined} */
  /** @type {string} */
  #tier = '';
  /** @type {ReturnType<typeof setTimeout> | null} */
  #copyTimer = null;
  /** @type {HTMLElement} */
  #nav = /** @type {*} */ (null);
  /** Whether #nav was generated by #renderPlatforms (vs. the author's slotted nav). */
  #navGenerated = false;

  get url() {
    return this.#url;
  }

  set url(val) {
    this.#url = val;
  }

  /** @returns {string} */
  get title() {
    return this.#title ?? '';
  }

  /** @param {string} val */
  set title(val) {
    this.#title = val;
  }

  get text() {
    return this.#text;
  }

  set text(val) {
    this.#text = val;
  }

  setup() {
    this.#resolveMeta();
    this.#detectTier();

    // Inside selection-menu: render minimal icon button
    if (this.closest('selection-menu')) {
      this.#renderSelectionAction();
      this.setAttribute('data-tier-resolved', 'selection');
      return;
    }

    if (this.#tier === 'native') {
      this.#renderNative();
    } else if (this.#tier === 'hidden') {
      this.style.display = 'none';
    } else if (this.children.length > 0) {
      this.#enhanceSlotted();
    } else {
      this.#renderPlatforms();
    }

    this.setAttribute('data-tier-resolved', this.#tier);
  }

  teardown() {
    this.removeAttribute('data-tier-resolved');
    if (this.#copyTimer) {
      clearTimeout(this.#copyTimer);
      this.#copyTimer = null;
    }
    if (this.#nav) {
      this.#nav.removeEventListener('click', this.#handlePlatformClick);
      // Only remove a nav we generated (#renderPlatforms). The slotted nav
      // (#enhanceSlotted) holds the author's links; setup() reuses it on
      // reconnect, so removing it would lose that content.
      if (this.#navGenerated) this.#nav.remove();
      this.#nav = /** @type {*} */ (null);
      this.#navGenerated = false;
    }
    // Remove the native share / selection buttons if they were rendered, so a
    // reconnect's setup() does not append a duplicate.
    this.querySelector(':scope > .share-trigger')?.remove();
    this.querySelector(':scope > .share-selection')?.remove();
  }

  /** Programmatically trigger share (native or first platform) */
  async share() {
    if (this.#tier === 'native') {
      await this.#doNativeShare();
    }
  }

  #resolveMeta() {
    // URL: attribute → canonical → location
    this.#url = this.getAttribute('url')
      || /** @type {HTMLLinkElement | null} */ (document.querySelector('link[rel~="canonical" i]'))?.href
      || location.href;

    // Title: attribute → og:title → document.title
    this.#title = this.getAttribute('title')
      || document.querySelector('meta[property="og:title"]')?.getAttribute('content')
      || document.title;

    // Text: attribute → meta description → og:description → empty
    this.#text = this.getAttribute('text')
      || document.querySelector('meta[name="description"]')?.getAttribute('content')
      || document.querySelector('meta[property="og:description"]')?.getAttribute('content')
      || '';
  }

  #detectTier() {
    // Explicit override: tier="platforms" forces Tier 2
    const override = this.getAttribute('tier');
    if (override === 'platforms' || override === 'native') {
      if (override === 'native' && !navigator.share) {
        this.#tier = 'hidden';
      } else {
        this.#tier = override;
      }
      return;
    }

    const platforms = this.getAttribute('platforms') || DEFAULT_PLATFORMS;

    if (platforms === 'native-only') {
      this.#tier = typeof navigator.share === 'function' ? 'native' : 'hidden';
      return;
    }

    this.#tier = typeof navigator.share === 'function' ? 'native' : 'platforms';
  }

  #renderSelectionAction() {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'share-selection';
    btn.setAttribute('aria-label', 'Share selection');
    const icon = document.createElement('icon-wc');
    icon.setAttribute('name', 'share-2');
    icon.setAttribute('size', 'sm');
    icon.setAttribute('aria-hidden', 'true');
    btn.appendChild(icon);
    btn.addEventListener('click', () => {
      const menu = /** @type {any} */ (this.closest('selection-menu'));
      const sel = menu?.getSelection?.();
      const text = sel?.text || window.getSelection()?.toString() || '';
      if (text && navigator.share) {
        navigator.share({ text: `"${text}" — ${location.href}` }).catch(() => {});
      } else if (text) {
        copyText(`"${text}" — ${location.href}`, { button: btn, announceMessage: 'Selection copied' });
      }
      menu?.dismiss?.();
    });
    this.appendChild(btn);
  }

  #renderNative() {
    const label = this.getAttribute('label') || 'Share';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'share-trigger';
    btn.setAttribute('aria-label', `Share this page`);
    btn.innerHTML = `<icon-wc name="share-2" size="sm" aria-hidden="true"></icon-wc><span class="share-label">${label}</span>`;
    btn.addEventListener('click', this.#handleNativeShare);
    this.appendChild(btn);
  }

  #renderPlatforms() {
    const platformList = (this.getAttribute('platforms') || DEFAULT_PLATFORMS)
      .split(',')
      .map(p => p.trim())
      .filter(Boolean);

    this.#nav = document.createElement('nav');
    this.#nav.setAttribute('aria-label', 'Share this page');

    for (const id of platformList) {
      const platform = PLATFORMS.get(id);
      if (!platform) continue;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.dataset.platform = id;
      btn.setAttribute('aria-label', id === 'copy' ? 'Copy link' : `Share on ${platform.label}`);

      const iconSet = platform.iconSet ? ` set="${platform.iconSet}"` : '';
      btn.innerHTML =
        `<icon-wc name="${platform.iconName}" size="sm"${iconSet} aria-hidden="true"></icon-wc>` +
        `<span class="share-label">${platform.label}</span>`;

      this.#nav.appendChild(btn);
    }

    this.#nav.addEventListener('click', this.#handlePlatformClick);
    this.appendChild(this.#nav);
    this.#navGenerated = true;
  }

  #enhanceSlotted() {
    // Wrap in nav if not already wrapped
    if (!this.querySelector('nav')) {
      const nav = document.createElement('nav');
      nav.setAttribute('aria-label', 'Share this page');
      while (this.firstChild) {
        nav.appendChild(this.firstChild);
      }
      this.appendChild(nav);
      this.#nav = nav;
    } else {
      this.#nav = /** @type {HTMLElement} */ (this.querySelector('nav'));
    }

    // Substitute URL tokens in href attributes
    const links = this.querySelectorAll('a[data-platform]');
    for (const link of links) {
      const href = link.getAttribute('href') || '';
      link.setAttribute('href',
        href
          .replace(/\{url\}/g, encodeURIComponent(this.#url || ''))
          .replace(/\{title\}/g, encodeURIComponent(this.#title || ''))
          .replace(/\{text\}/g, encodeURIComponent(this.#text || ''))
      );
    }

    // Add click handlers for copy buttons
    const copyBtns = this.querySelectorAll('[data-platform="copy"]');
    for (const btn of copyBtns) {
      btn.addEventListener('click', this.#handleCopy);
    }

    this.#nav.addEventListener('click', this.#handlePlatformClick);
  }

  #handleNativeShare = async () => {
    await this.#doNativeShare();
  };

  async #doNativeShare() {
    const payload = { url: this.#url || '', title: this.#title || '', text: this.#text || '' };

    this.dispatchEvent(new CustomEvent('share-wc:open', {
      detail: { platform: 'native' },
      bubbles: true,
    }));

    try {
      if (navigator.canShare && !navigator.canShare(payload)) {
        throw new Error('Payload not shareable');
      }
      await navigator.share(payload);
      this.dispatchEvent(new CustomEvent('share-wc:success', {
        detail: { platform: 'native' },
        bubbles: true,
      }));
    } catch (err) {
      if (err.name !== 'AbortError') {
        this.dispatchEvent(new CustomEvent('share-wc:error', {
          detail: { platform: 'native', error: err },
          bubbles: true,
        }));
      }
    }
  }

  #handlePlatformClick = (e) => {
    const btn = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (e.target).closest('[data-platform]'));
    if (!btn) return;

    const id = btn.dataset.platform;

    if (id === 'copy') {
      this.#handleCopy(e, btn);
      return;
    }

    // For slotted <a> elements, let the default action happen
    if (btn.tagName === 'A') return;

    if (!id) return;
    const platform = PLATFORMS.get(id);
    if (!platform) return;

    this.dispatchEvent(new CustomEvent('share-wc:open', {
      detail: { platform: id },
      bubbles: true,
    }));

    const opts = { url: this.#url || '', title: this.#title || '', text: this.#text || '' };

    if (id === 'email') {
      location.href = platform.buildUrl(opts);
    } else if (id === 'mastodon') {
      const instance = this.getAttribute('mastodon-instance') || 'mastodon.social';
      const url = /** @type {(opts: { url: string, title: string, text: string }, instance?: string) => string} */ (platform.buildUrl)(opts, instance);
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      window.open(platform.buildUrl(opts), '_blank', 'noopener,noreferrer');
    }

    this.dispatchEvent(new CustomEvent('share-wc:success', {
      detail: { platform: id },
      bubbles: true,
    }));
  };

  /**
   * @param {Event} _e
   * @param {HTMLElement} [target]
   */
  #handleCopy = async (_e, target) => {
    const btn = target || /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (_e?.target)?.closest('[data-platform="copy"]'));
    if (!btn) return;

    const ok = await copyText(this.#url || '', {
      button: btn,
      announceMessage: 'Link copied to clipboard',
      duration: 2000,
    });

    if (!ok) {
      this.dispatchEvent(new CustomEvent('share-wc:error', {
        detail: { platform: 'copy' },
        bubbles: true,
      }));
      return;
    }

    // Visible label swap (data-state already set by copyText drives any CSS hook)
    const label = btn.querySelector('.share-label');
    const originalText = label?.textContent;
    if (label) label.textContent = 'Copied!';

    this.dispatchEvent(new CustomEvent('share-wc:success', {
      detail: { platform: 'copy' },
      bubbles: true,
    }));

    if (this.#copyTimer) clearTimeout(this.#copyTimer);
    this.#copyTimer = setTimeout(() => {
      if (label && originalText) label.textContent = originalText;
      this.#copyTimer = null;
    }, 2000);
  };
}

registerComponent('share-wc', /** @type {typeof HTMLElement} */ (/** @type {unknown} */ (ShareWc)));

export { ShareWc };
